import { DEFAULT_REPLAY_ARTIFACT_LIMITS } from "@air-hockey/game-core";
import type { CreatedMatch, MatchConnection, PlayerInfo, PlayerSlot } from "./types";
import { leagueForRating } from "./league";

interface PlayerIdentity {
  id: string;
  name: string;
}

interface CreateMatchOptions {
  player: PlayerIdentity;
  scoreToWin?: number;
  durationMs?: number;
  signal?: AbortSignal;
}

interface JoinMatchOptions {
  joinCode: string;
  player: PlayerIdentity;
  signal?: AbortSignal;
}

export type MatchmakingMode = "casual" | "ranked";
export type MatchmakingStatus = "searching" | "matched" | "cancelled" | "expired";

export interface DeviceSession {
  accessToken: string;
  expiresAt: string;
  player: {
    id: string;
    name: string;
  };
  capabilities: {
    matchmakingModes: MatchmakingMode[];
  };
}

export interface DeviceBootstrapRequest {
  deviceId: string;
  deviceSecret: string;
  idempotencyKey: string;
  username?: string;
  signal?: AbortSignal;
}

export interface MatchmakingTicket {
  ticketId: string;
  searchTicket: string;
  status: MatchmakingStatus;
  mode: MatchmakingMode;
  region: string;
  createdAt: string | null;
  expiresAt: string | null;
  pollAfterMs: number;
  position: number | null;
  estimatedWaitSeconds: number | null;
  connection: MatchConnection | null;
  opponent: { id: string; name: string } | null;
}

export interface PlayerProfile {
  playerId: string;
  name: string;
  rating: number;
  rank: number | null;
  league: string;
  wins: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  winStreak: number;
  matchesPlayed: number;
}

export interface LeaderboardEntry {
  rank: number;
  playerId: string;
  name: string;
  rating: number;
  wins: number;
  losses: number;
}

export interface MatchHistoryEntry {
  matchId: string;
  finishedAt: string;
  mode: string;
  opponentId: string;
  opponentName: string;
  scoreFor: number;
  scoreAgainst: number;
  result: "win" | "loss" | "draw";
  ratingDelta: number | null;
}

export type ReplayFetchResult =
  | { status: "ready"; rawJson: string }
  | { status: "not-ready"; message: string; retryAfterMs: number | null }
  | { status: "not-found"; message: string };

export type ReportReason =
  | "cheating"
  | "abuse"
  | "name"
  | "disconnect"
  | "exploit"
  | "collusion"
  | "harassment";

export interface MatchReportRequest {
  matchId: string;
  reportedUserId: string;
  reason: ReportReason;
  details: string;
  session: DeviceSession;
  idempotencyKey: string;
  signal?: AbortSignal;
}

const JOIN_IDEMPOTENCY_PREFIX = "air-hockey.join-idempotency.v1";
const joinIdempotencyMemory = new Map<string, string>();

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null,
    readonly retryable = false,
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function getApiBaseUrl(override?: string): string {
  const configured = override ?? import.meta.env.VITE_API_URL;
  const fallback = typeof window === "undefined" ? "http://127.0.0.1:8787" : window.location.origin;
  return (configured?.trim() || fallback).replace(/\/+$/, "");
}

export async function bootstrapDeviceSession({
  deviceId,
  deviceSecret,
  idempotencyKey,
  username,
  signal,
}: DeviceBootstrapRequest): Promise<DeviceSession> {
  const payload = await requestJson("/api/auth/device", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      deviceId,
      deviceSecret,
      ...(username ? { username } : {}),
    }),
    ...(signal ? { signal } : {}),
  }, "Could not start a device session.");
  return normalizeDeviceSession(payload);
}

export async function startMatchmaking(
  session: DeviceSession,
  mode: MatchmakingMode,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<MatchmakingTicket> {
  const payload = await requestJson("/api/matchmaking/searches", {
    method: "POST",
    headers: {
      ...authenticatedHeaders(session, true),
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({ mode, region: "AUTO" }),
    ...(signal ? { signal } : {}),
  }, "Could not enter matchmaking.");
  return normalizeMatchmakingTicket(payload, mode);
}

export async function pollMatchmaking(
  session: DeviceSession,
  ticketId: string,
  searchTicket: string,
  mode: MatchmakingMode,
  signal?: AbortSignal,
): Promise<MatchmakingTicket> {
  const payload = await requestJson(`/api/matchmaking/searches/${encodeURIComponent(ticketId)}`, {
    method: "GET",
    headers: {
      ...authenticatedHeaders(session),
      "X-Search-Ticket": searchTicket,
    },
    ...(signal ? { signal } : {}),
  }, "Could not refresh matchmaking.");
  return normalizeMatchmakingTicket(payload, mode, searchTicket);
}

export async function cancelMatchmaking(
  session: DeviceSession,
  ticketId: string,
  searchTicket: string,
  signal?: AbortSignal,
): Promise<void> {
  await requestJson(`/api/matchmaking/searches/${encodeURIComponent(ticketId)}`, {
    method: "DELETE",
    headers: {
      ...authenticatedHeaders(session),
      "X-Search-Ticket": searchTicket,
    },
    ...(signal ? { signal } : {}),
  }, "Could not cancel matchmaking.", true);
}

export async function getPlayerProfile(session: DeviceSession, signal?: AbortSignal): Promise<PlayerProfile> {
  const payload = await requestJson("/api/profile", {
    method: "GET",
    headers: authenticatedHeaders(session),
    ...(signal ? { signal } : {}),
  }, "Could not load the profile.");
  return normalizePlayerProfile(payload);
}

export async function getMatchHistory(
  session: DeviceSession,
  signal?: AbortSignal,
): Promise<MatchHistoryEntry[]> {
  const payload = await requestJson("/api/history?limit=20", {
    method: "GET",
    headers: authenticatedHeaders(session),
    ...(signal ? { signal } : {}),
  }, "Could not load match history.");
  const record = asRecord(payload);
  const items = Array.isArray(payload) ? payload : Array.isArray(record.matches) ? record.matches : [];
  return items.map(normalizeHistoryEntry).filter((entry): entry is MatchHistoryEntry => entry !== null);
}

export async function getMatchHistoryEntry(
  session: DeviceSession,
  matchId: string,
  signal?: AbortSignal,
): Promise<MatchHistoryEntry> {
  const payload = await requestJson(`/api/history/${encodeURIComponent(matchId)}`, {
    method: "GET",
    headers: authenticatedHeaders(session),
    ...(signal ? { signal } : {}),
  }, "Could not verify this match for reporting.");
  const entry = normalizeHistoryEntry(payload);
  if (!entry) throw new Error("The match history response was incomplete.");
  return entry;
}

/** Fetches the canonical replay document without normalizing or reserializing it. */
export async function getReplayArtifact(
  session: DeviceSession,
  matchId: string,
  signal?: AbortSignal,
): Promise<ReplayFetchResult> {
  const response = await fetch(
    `${getApiBaseUrl()}/api/replays/${encodeURIComponent(matchId)}`,
    {
      method: "GET",
      headers: authenticatedHeaders(session),
      ...(signal ? { signal } : {}),
    },
  );
  const rawJson = await readBoundedText(response, DEFAULT_REPLAY_ARTIFACT_LIMITS.maxBytes);

  if (response.status === 202) {
    return {
      status: "not-ready",
      message: responseMessage(rawJson, "This verified replay is still being prepared."),
      retryAfterMs: retryAfterMilliseconds(response.headers.get("Retry-After")),
    };
  }
  if (response.status === 404) {
    return {
      status: "not-found",
      message: responseMessage(rawJson, "No verified replay is available for this match."),
    };
  }
  if (!response.ok) {
    const payload = parseLooseJson(rawJson);
    if (response.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event("air-hockey:session-invalid"));
    }
    throw apiError(response.status, payload, "Could not load this replay.");
  }
  return { status: "ready", rawJson };
}

export async function getLeaderboard(
  session: DeviceSession,
  signal?: AbortSignal,
): Promise<LeaderboardEntry[]> {
  const payload = await requestJson("/api/leaderboard?season=current&limit=50", {
    method: "GET",
    headers: authenticatedHeaders(session),
    ...(signal ? { signal } : {}),
  }, "Could not load the leaderboard.");
  const record = asRecord(payload);
  const items = Array.isArray(payload) ? payload : Array.isArray(record.entries) ? record.entries : [];
  return items.map(normalizeLeaderboardEntry).filter((entry): entry is LeaderboardEntry => entry !== null);
}

export async function submitMatchReport({
  matchId,
  reportedUserId,
  reason,
  details,
  session,
  idempotencyKey,
  signal,
}: MatchReportRequest): Promise<void> {
  await requestJson("/api/reports", {
    method: "POST",
    headers: {
      ...authenticatedHeaders(session, true),
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      matchId,
      reportedUserId,
      category: reason,
      ...(details.trim() ? { details: details.trim() } : {}),
    }),
    ...(signal ? { signal } : {}),
  }, "Could not submit the report.", true);
}

export async function createPrivateMatch({
  player,
  scoreToWin = 7,
  durationMs,
  signal,
}: CreateMatchOptions): Promise<CreatedMatch> {
  const response = await fetch(`${getApiBaseUrl()}/api/matches`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      host: { id: player.id, name: player.name },
      rules: {
        scoreToWin,
        ...(durationMs === undefined ? {} : { durationMs }),
      },
    }),
    ...(signal ? { signal } : {}),
  });
  const payload: unknown = await readJson(response);
  if (!response.ok) throw apiError(response.status, payload, "Could not create the match.");
  return normalizeCreatedMatch(payload);
}

export async function joinPrivateMatch({ joinCode, player, signal }: JoinMatchOptions): Promise<MatchConnection> {
  const normalizedCode = joinCode.trim().toUpperCase();
  const idempotencyKey = getJoinIdempotencyKey(normalizedCode, player.id);
  const response = await fetch(`${getApiBaseUrl()}/api/matches/join`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      joinCode: normalizedCode,
      player: { id: player.id, name: player.name },
    }),
    ...(signal ? { signal } : {}),
  });
  const payload: unknown = await readJson(response);
  if (!response.ok) throw apiError(response.status, payload, "Could not join that match.");
  return normalizeConnection(payload, "B");
}

function getJoinIdempotencyKey(joinCode: string, playerId: string): string {
  const storageKey = `${JOIN_IDEMPOTENCY_PREFIX}:${joinCode}:${playerId}`;
  const inMemory = joinIdempotencyMemory.get(storageKey);
  if (inMemory) return inMemory;
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (stored && /^[A-Za-z0-9_-]{43}$/.test(stored)) {
      joinIdempotencyMemory.set(storageKey, stored);
      return stored;
    }
  } catch {
    // The in-memory key below still protects retries when storage is blocked.
  }

  const generated = createOpaqueKey();
  joinIdempotencyMemory.set(storageKey, generated);
  try {
    sessionStorage.setItem(storageKey, generated);
  } catch {
    // A stable in-memory copy remains available for this page lifetime.
  }
  return generated;
}

export function createOpaqueKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function normalizeCreatedMatch(payload: unknown): CreatedMatch {
  const record = asRecord(payload);
  const players = asRecord(record.players);
  const host = asRecord(record.host);
  const opponent = asRecord(record.opponent);
  const playerA = normalizePlayer(Object.keys(players).length > 0 ? players.A : host, "A");
  const maybeB = Object.keys(players).length > 0 ? players.B : opponent;
  const playerB = maybeB ? normalizePlayer(maybeB, "B") : undefined;
  const matchId = stringValue(record.matchId, "matchId");

  return {
    matchId,
    joinCode: typeof record.joinCode === "string" ? record.joinCode : matchId,
    status: typeof record.status === "string" ? record.status : "waiting",
    protocolVersion: typeof record.protocolVersion === "string" ? record.protocolVersion : "unknown",
    ticketExpiresAt: typeof record.ticketExpiresAt === "string" ? record.ticketExpiresAt : "",
    players: playerB ? { A: playerA, B: playerB } : { A: playerA },
  };
}

export function normalizeConnection(payload: unknown, fallbackSlot: PlayerSlot): MatchConnection {
  const record = asRecord(payload);
  const players = asRecord(record.players);
  const playerRecord = record.player ?? record.connection ?? players[fallbackSlot] ?? record.host;
  const player = normalizePlayer(playerRecord, fallbackSlot);

  return {
    matchId: stringValue(record.matchId, "matchId"),
    ...(typeof record.status === "string" ? { status: record.status } : {}),
    protocolVersion: typeof record.protocolVersion === "string" ? record.protocolVersion : "unknown",
    ticketExpiresAt: typeof record.ticketExpiresAt === "string" ? record.ticketExpiresAt : "",
    player,
  };
}

export function resolveWebSocketUrl(wsUrl: string): string {
  const url = new URL(wsUrl, getApiBaseUrl());
  if (url.protocol === "http:") url.protocol = "ws:";
  if (url.protocol === "https:") url.protocol = "wss:";
  return url.toString();
}

function normalizePlayer(value: unknown, fallbackSlot: PlayerSlot): PlayerInfo {
  const record = asRecord(value);
  const playerId = record.playerId ?? record.id;
  return {
    playerId: stringValue(playerId, "playerId"),
    name: typeof record.name === "string" ? record.name : fallbackSlot === "A" ? "Host" : "Guest",
    slot: record.slot === "A" || record.slot === "B" ? record.slot : fallbackSlot,
    joinTicket: stringValue(record.joinTicket ?? record.ticket, "joinTicket"),
    wsUrl: stringValue(record.wsUrl, "wsUrl"),
  };
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text };
  }
}

async function readBoundedText(response: Response, maximumBytes: number): Promise<string> {
  const advertisedLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(advertisedLength) && advertisedLength > maximumBytes) {
    throw new ApiError("The replay is too large to open safely.", 413, "REPLAY_TOO_LARGE");
  }
  if (!response.body) {
    const text = await response.text();
    if (new TextEncoder().encode(text).byteLength > maximumBytes) {
      throw new ApiError("The replay is too large to open safely.", 413, "REPLAY_TOO_LARGE");
    }
    return text;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let receivedBytes = 0;
  let text = "";
  try {
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      receivedBytes += result.value.byteLength;
      if (receivedBytes > maximumBytes) {
        await reader.cancel();
        throw new ApiError("The replay is too large to open safely.", 413, "REPLAY_TOO_LARGE");
      }
      text += decoder.decode(result.value, { stream: true });
    }
    text += decoder.decode();
    return text;
  } finally {
    reader.releaseLock();
  }
}

function parseLooseJson(value: string): unknown {
  if (!value) return {};
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return { message: value };
  }
}

function responseMessage(value: string, fallback: string): string {
  const record = asRecord(parseLooseJson(value));
  const envelope = asRecord(record.error);
  if (typeof envelope.message === "string") return envelope.message;
  if (typeof record.message === "string") return record.message;
  if (typeof record.error === "string") return record.error;
  return fallback;
}

function retryAfterMilliseconds(value: string | null): number | null {
  if (value === null) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1_000, 60_000);
  const at = Date.parse(value);
  return Number.isFinite(at) ? Math.min(60_000, Math.max(0, at - Date.now())) : null;
}

function apiError(status: number, payload: unknown, fallback: string): ApiError {
  const record = asRecord(payload);
  const envelope = asRecord(record.error);
  const message =
    typeof envelope.message === "string"
      ? envelope.message
      : typeof record.message === "string"
        ? record.message
        : typeof record.error === "string"
          ? record.error
          : fallback;
  return new ApiError(
    message,
    status,
    typeof envelope.code === "string" ? envelope.code : null,
    envelope.retryable === true,
    typeof envelope.requestId === "string" ? envelope.requestId : null,
  );
}

async function requestJson(
  path: string,
  init: RequestInit,
  fallbackError: string,
  allowEmpty = false,
): Promise<unknown> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, init);
  if (allowEmpty && response.status === 204) return {};
  const payload = await readJson(response);
  if (!response.ok) {
    if (response.status === 401 && path !== "/api/auth/device" && typeof window !== "undefined") {
      window.dispatchEvent(new Event("air-hockey:session-invalid"));
    }
    throw apiError(response.status, payload, fallbackError);
  }
  return payload;
}

function authenticatedHeaders(session: DeviceSession, json = false): Record<string, string> {
  return {
    Authorization: `Bearer ${session.accessToken}`,
    ...(json ? { "content-type": "application/json" } : {}),
  };
}

function normalizeDeviceSession(payload: unknown): DeviceSession {
  const record = asRecord(payload);
  const user = asRecord(record.user);
  const profile = asRecord(record.profile);
  const playerId = stringValue(user.id ?? user.userId ?? profile.userId, "user.id");
  const name = stringValue(user.username ?? user.name ?? profile.username, "user.username");
  return {
    accessToken: stringValue(record.token ?? record.accessToken, "token"),
    expiresAt: dateValue(record.expiresAt),
    player: { id: playerId, name },
    capabilities: { matchmakingModes: ["casual", "ranked"] },
  };
}

function normalizeMatchmakingTicket(
  payload: unknown,
  fallbackMode: MatchmakingMode,
  fallbackSearchTicket?: string,
): MatchmakingTicket {
  const record = asRecord(payload);
  const status = isMatchmakingStatus(record.status) ? record.status : "searching";
  const mode = record.mode === "ranked" || record.mode === "casual" ? record.mode : fallbackMode;
  const match = asRecord(record.match);
  return {
    ticketId: stringValue(record.searchId ?? record.ticketId, "searchId"),
    searchTicket: stringValue(record.searchTicket ?? fallbackSearchTicket, "searchTicket"),
    status,
    mode,
    region: typeof record.region === "string" ? record.region : "AUTO",
    createdAt: nullableDateValue(record.queuedAt ?? record.createdAt),
    expiresAt: nullableDateValue(record.expiresAt),
    pollAfterMs: typeof record.pollAfterMs === "number" ? clampNumber(record.pollAfterMs, 250, 5_000) : 1_000,
    position: nullableNumber(record.position),
    estimatedWaitSeconds: nullableNumber(record.estimatedWaitSeconds ?? record.estimatedWait),
    connection: status === "matched" ? normalizeQueueConnection(match, record) : null,
    opponent: normalizeOpponent(record.opponent ?? match.opponent),
  };
}

function normalizeQueueConnection(match: Record<string, unknown>, parent: Record<string, unknown>): MatchConnection | null {
  if (Object.keys(match).length === 0) return null;
  try {
    const playerRecord = asRecord(match.player ?? match.connection);
    const player = normalizePlayer(playerRecord, "A");
    const wsUrl = new URL(player.wsUrl, getApiBaseUrl());
    return {
      matchId: stringValue(match.matchId ?? parent.matchId, "matchId"),
      status: "matched",
      protocolVersion:
        typeof match.protocolVersion === "string"
          ? match.protocolVersion
          : wsUrl.searchParams.get("protocolVersion") ?? "unknown",
      ticketExpiresAt:
        typeof playerRecord.ticketExpiresAt === "string"
          ? playerRecord.ticketExpiresAt
          : dateValue(playerRecord.joinTicketExpiresAt),
      player,
    };
  } catch {
    return null;
  }
}

function normalizePlayerProfile(payload: unknown): PlayerProfile {
  const record = asRecord(payload);
  const user = asRecord(record.user);
  const profile = Object.keys(asRecord(record.profile)).length > 0 ? asRecord(record.profile) : record;
  const season = asRecord(profile.season);
  const wins = numericValue(profile.wins);
  const losses = numericValue(profile.losses);
  const rating = numericValue(season.rating ?? profile.rating ?? profile.seasonRating, 1_500);
  return {
    playerId: stringValue(profile.userId ?? profile.playerId ?? user.id, "profile.userId"),
    name: stringValue(profile.username ?? user.username ?? profile.name, "profile.username"),
    rating,
    rank: nullableNumber(profile.rank ?? profile.leaderboardRank),
    league: typeof profile.league === "string" ? profile.league : leagueForRating(rating),
    wins,
    losses,
    goalsFor: numericValue(profile.goalsFor ?? profile.goalsScored),
    goalsAgainst: numericValue(profile.goalsAgainst ?? profile.goalsConceded),
    winStreak: numericValue(profile.currentWinStreak ?? profile.winStreak),
    matchesPlayed: numericValue(profile.matchesPlayed, wins + losses),
  };
}

function normalizeLeaderboardEntry(value: unknown): LeaderboardEntry | null {
  const record = asRecord(value);
  const playerId = record.playerId ?? record.userId ?? record.id;
  const name = record.name ?? record.username;
  if (typeof playerId !== "string" || typeof name !== "string") return null;
  return {
    rank: numericValue(record.rank),
    playerId,
    name,
    rating: numericValue(record.rating ?? record.seasonRating, 1_000),
    wins: numericValue(record.wins),
    losses: numericValue(record.losses),
  };
}

function normalizeHistoryEntry(value: unknown): MatchHistoryEntry | null {
  const record = asRecord(value);
  const matchId = record.matchId ?? record.id;
  if (typeof matchId !== "string") return null;
  const slot = record.player === "B" ? "B" : "A";
  const scoreFor = numericValue(
    record.scoreFor ?? record.playerScore ?? (slot === "A" ? record.scoreA : record.scoreB),
  );
  const scoreAgainst = numericValue(
    record.scoreAgainst ?? record.opponentScore ?? (slot === "A" ? record.scoreB : record.scoreA),
  );
  const opponent = asRecord(record.opponent);
  const opponentId = record.opponentId ?? opponent.id ?? opponent.userId;
  if (typeof opponentId !== "string") return null;
  const explicitResult = record.result;
  const result =
    explicitResult === "win" || explicitResult === "loss" || explicitResult === "draw"
      ? explicitResult
      : typeof record.winnerId === "string"
        ? record.winnerId === opponentId
          ? "loss"
          : "win"
      : scoreFor === scoreAgainst
        ? "draw"
        : scoreFor > scoreAgainst
          ? "win"
          : "loss";
  return {
    matchId,
    finishedAt: dateValue(record.finishedAt ?? record.createdAt),
    mode: typeof record.mode === "string" ? record.mode : "casual",
    opponentId,
    opponentName:
      typeof record.opponentName === "string"
        ? record.opponentName
        : typeof opponent.username === "string"
          ? opponent.username
          : "Opponent",
    scoreFor,
    scoreAgainst,
    result,
    ratingDelta: nullableNumber(record.ratingDelta),
  };
}

function normalizeOpponent(value: unknown): { id: string; name: string } | null {
  const record = asRecord(value);
  const id = record.id ?? record.playerId ?? record.userId;
  const name = record.name ?? record.username;
  return typeof id === "string" && typeof name === "string" ? { id, name } : null;
}

function isMatchmakingStatus(value: unknown): value is MatchmakingStatus {
  return value === "searching" || value === "matched" || value === "cancelled" || value === "expired";
}

function numericValue(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function clampNumber(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function dateValue(value: unknown): string {
  if (typeof value === "string" && value.length > 0) return value;
  if (typeof value === "number" && Number.isFinite(value)) return new Date(value).toISOString();
  return new Date(0).toISOString();
}

function nullableDateValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return dateValue(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function stringValue(value: unknown, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`The server response is missing ${field}.`);
  }
  return value;
}
